/**
 * Vehicle Service - Unified service for both web and mobile platforms
 * Handles vehicle-related operations including VIN decoding
 */
export class VehicleService {
    constructor(client) {
        this.client = client;
    }
    static getInstance(client) {
        if (!VehicleService.instances.has(client)) {
            VehicleService.instances.set(client, new VehicleService(client));
        }
        return VehicleService.instances.get(client);
    }
    /**
     * Get thumbnail key for a vehicle VIN
     * Always uses .jpg extension for consistency
     */
    getThumbnailKey(vin) {
        return `vehicles/${vin}/thumbnail.jpg`;
    }
    /**
     * Get vehicle by VIN
     * Returns complete vehicle record if exists
     */
    async getVehicleByVin(vin) {
        try {
            const response = await this.client.get(`/vehicles/vin/${encodeURIComponent(vin)}`);
            if (!response.success || !response.data) {
                throw new Error(response.error || 'Failed to fetch vehicle');
            }
            return response.data;
        }
        catch (error) {
            console.error('Get vehicle by VIN error:', error);
            throw error;
        }
    }
    /**
     * Set vehicle thumbnail from photo URL (download + upload)
     * LEGACY METHOD - Only used as fallback when server-side copy not available
     * Prefer setVehicleThumbnailFromKey() for efficiency
     */
    async setVehicleThumbnail(vin, photoUrl) {
        try {
            const blob = await this.client.downloadFile(photoUrl);
            const thumbnailKey = this.getThumbnailKey(vin);
            const encodedThumbnailKey = encodeURIComponent(thumbnailKey);
            if (this.client.delete) {
                const deleteResult = await this.client.delete(`/files/${encodedThumbnailKey}`);
            }
            const uploadPath = `/files/upload-to/${encodedThumbnailKey}`;
            if (!this.client.uploadFile) {
                throw new Error('Upload method not available');
            }
            const uploadResult = await this.client.uploadFile(uploadPath, blob, 'thumbnail.jpg');
            if (!uploadResult.success) {
                throw new Error('Upload failed: ' + uploadResult.error);
            }
            await new Promise((resolve) => setTimeout(resolve, 500));
        }
        catch (error) {
            console.error('[VehicleService] setVehicleThumbnail error:', error);
            throw error;
        }
    }
    /**
     * Set vehicle thumbnail from existing file key (server-side copy)
     * More efficient - no download/upload, server copies file internally
     * Falls back to download+upload method if backend endpoint not available
     */
    async setVehicleThumbnailFromKey(vin, sourceFileKey) {
        try {
            const response = await this.client.post(`/vehicles/vin/${encodeURIComponent(vin)}/thumbnail`, {
                source_file_key: sourceFileKey,
            });
            if (!response.success) {
                throw new Error(response.error || 'Failed to set thumbnail from key');
            }
            await new Promise((resolve) => setTimeout(resolve, 500));
        }
        catch (error) {
            const baseUrl = this.client.getBaseUrl?.() || '';
            const fileUrl = `${baseUrl}/files/render/${sourceFileKey}`;
            await this.setVehicleThumbnail(vin, fileUrl);
        }
    }
    /**
     * Decode VIN using backend API
     * Returns vehicle information (make, model, year)
     */
    async decodeVin(vin) {
        try {
            const response = await this.client.get(`/vehicles/vin/${encodeURIComponent(vin)}/decode-full`);
            if (!response.success || !response.data) {
                return null;
            }
            // Everything useful the record carries, not just the three fields a
            // fresh NHTSA decode happens to fill. For a VIN we have seen before this
            // is where the stock number, colour and mileage come from.
            const data = response.data;
            return {
                make: data.make,
                model: data.model,
                year: data.year,
                trim: data.trim,
                color: data.color ?? undefined,
                mileage: data.mileage ?? undefined,
                stock_number: data.stock_number ?? undefined,
            };
        }
        catch (error) {
            return null;
        }
    }
    /**
     * Extract VIN from an image using backend AI-powered extraction
     * Uses tiered approach: barcode detection → local OCR → AI vision
     *
     * @param imageBlob - The image file as a Blob
     * @param options - Extraction options (tier preference, skip cache)
     * @returns Extraction result with VIN, confidence, and method used
     */
    async decodeVINFromImage(imageBlob, options) {
        try {
            // Build form data
            const formData = new FormData();
            // React Native passes { uri, type, name } objects instead of Blobs.
            // In RN, formData.append(key, file) must use the 2-arg form — the 3-arg
            // overload (with filename) is a web-only API and silently breaks on RN.
            const isRNFileObject = imageBlob && typeof imageBlob.uri === 'string';
            if (isRNFileObject) {
                // React Native: append the { uri, type, name } object directly (2-arg form)
                formData.append('file', imageBlob);
                console.log('[VehicleService] Appending RN file object, uri:', imageBlob.uri?.substring(0, 60));
            }
            else {
                // Web: standard Blob with optional filename override
                const contentType = imageBlob.type || 'image/jpeg';
                const extension = contentType.includes('png') ? 'png' : contentType.includes('webp') ? 'webp' : 'jpg';
                formData.append('file', imageBlob, `vin-image.${extension}`);
            }
            // Add optional parameters
            if (options?.tierPreference) {
                formData.append('tier_preference', options.tierPreference);
            }
            if (options?.skipCache) {
                formData.append('skip_cache', 'true');
            }
            const signal = options?.signal;
            const traceHeaders = options?.traceparent ? { traceparent: options.traceparent } : undefined;
            // Use uploadMultipart if available, otherwise fall back to custom fetch
            if (this.client.uploadMultipart) {
                console.log('[VehicleService] Sending VIN image via uploadMultipart');
                const response = await this.client.uploadMultipart('/vehicles/vin/decode-image', formData, signal, traceHeaders);
                if (!response.success || !response.data) {
                    console.error('[VehicleService] VIN image extraction failed:', response.error);
                    return null;
                }
                return response.data;
            }
            // Fallback: Direct fetch with FormData
            const baseUrl = this.client.getBaseUrl?.() || '';
            const url = `${baseUrl}/vehicles/vin/decode-image`;
            console.log('[VehicleService] Sending VIN image via fetch to:', url);
            const response = await fetch(url, {
                method: 'POST',
                body: formData,
                credentials: 'include',
                signal,
                headers: traceHeaders,
            });
            if (!response.ok) {
                const errorText = await response.text();
                console.error('[VehicleService] VIN image extraction failed:', response.status, errorText);
                return null;
            }
            const data = await response.json();
            // Handle both ApiResponse<VINImageExtractionResult> and bare VINImageExtractionResult
            if (data && typeof data === 'object' && 'success' in data) {
                if (!data.success || !data.data) {
                    console.error('[VehicleService] VIN image extraction failed:', data.error);
                    return null;
                }
                return data.data;
            }
            return data;
        }
        catch (error) {
            console.error('[VehicleService] VIN image extraction error:', error instanceof Error ? `${error.message} (${error.name})` : error);
            return null;
        }
    }
}
VehicleService.instances = new Map();
/**
 * Create vehicle service instance (Singleton per API client)
 * Must be called with platform-specific API client
 */
export function createVehicleService(apiClient) {
    return VehicleService.getInstance(apiClient);
}
